import { useForm } from "antd/lib/form/Form";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../shared/types/common";
import { Drawer, Upload, UploadFile } from "antd";
import GenericFormComponent from "../../../shared/components/generic-form/generic-form.component";
import { showNotification } from "../../../shared/utils/utils";
import { API_GET_DOCUMENT_SIGNED_URL } from "../../../shared/routes/private.routes";
import {
  createProject,
  updateProjectById,
} from "../../../core/store/reducers/project.slice";
import { useEffect, useState } from "react";
import {
  API_GET_ALL_ACCOUNTS_LIST,
  API_GET_ALL_DEPARTMENT_LIST,
  API_GET_ALL_STAFF_LIST,
} from "../../../shared/routes/private.routes";
import { Client } from "../../../core/api-client";
import dayjs from "dayjs";
import {
  getDocuments,
  getSignedUrl,
} from "../../../core/store/reducers/activity-log.slice";
import { InboxOutlined } from "@ant-design/icons";
import { getAccountsByEntity } from "../../../core/store/reducers/accounts.slice";

interface Props {
  onClose: () => void;
  visible: boolean;
  selectedEntity?: any;
  onSubmit?: () => void;
  currencies: any;
  projectTypes: any;
  entityList: any;
}
const CreateEditProjectComponent = ({
  onClose,
  visible,
  selectedEntity = null,
  onSubmit = () => {
    return;
  },
  entityList,
  projectTypes,
  currencies,
}: Props) => {
  const dispatch = useDispatch();
  const [form] = useForm();
  const { screenType } = useSelector((state: RootState) => state.settings);
  const [staffList, setStaffList] = useState<any[]>([]);
  const [departmentList, setDepartmentList] = useState<any[]>([]);
  const [selectedStartDate, setSelectedStartDate] = useState<any>(null);
  const [accountList, setAccountList] = useState<any[]>([]);
  const [fileList, setFileList] = useState<UploadFile<any>[]>([]);
  const [documents, setDocuments] = useState<any[]>([]);

  const toDateOnly = (value: any) => {
    if (!value) return null;
    return dayjs(value).isValid() ? dayjs(value).format("YYYY-MM-DD") : null;
  };

  const onFinish = (values: any) => {
    // Split fileList into two groups
    const oldFiles = fileList.filter((file) => !file.originFileObj); // previously uploaded
    const newFiles = fileList.filter((file) => file.originFileObj); // new uploads
    const payload = {
      entity: values?.entity,
      projectType: values?.projectType,
      projectName: values?.l2projectName,
      l2ProjectName: values?.l2projectName,
      sowNumber: values?.sowNumber,
      poNumber: values?.poNumber,
      projectCode: values?.projectCode,
      poCurrency: values?.poCurrency,
      poValue: values?.poValue,
      sst: values?.sst,
      margin: values?.margin,
      projectValue: values?.projectValue,
      startDate: toDateOnly(values?.startDate),
      endDate: toDateOnly(values?.endDate),
      departmentId: values?.departmentName,
      projectManagerId: values?.projectManager,
      accountManagerId: values?.accountManager,
      accountId: values?.account,
      status: selectedEntity?.status === "DRAFT" ? "DRAFT" : selectedEntity ? values?.status : "DRAFT",
    };

    const formData = new FormData();

    Object.entries(payload).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(key, value);
      }
    });
    // Append file (assuming single file upload)
    if (fileList && newFiles.length > 0) {
      newFiles.forEach((fileItem) => {
        const file = fileItem;
        formData.append("files", file.originFileObj as File); // use consistent key name
      });
    }
    const remainingOldFiles = oldFiles.map((file) => ({
      uid: file.uid,
      name: file.name,
      url: file.url,
    }));
    formData.append("oldFiles", JSON.stringify(remainingOldFiles));
    if (selectedEntity) {
      dispatch(
        updateProjectById({
          projectId: selectedEntity.projectId,
          formData,
        })
      ).then((val: any) => {
        if (val?.payload?.data && val?.payload?.data?.statusCode === 200) {
          showNotification("Project Details Updated Successfully", "success");
          onSubmit();
        }
        if (val?.payload?.data && val?.payload?.data?.statusCode === 400) {
          if (val?.payload?.data?.message === "PONumberAlreadyExistMessage") {
            showNotification("PO Number Already Exist", "warning");
          } else if (val?.payload?.data?.message === "SOWNumberAlreadyExistMessage") {
            showNotification("SOW Number Already Exist", "warning");
          } else if (
            val?.payload?.data?.message === "projectCodeAlreadyExistMessage"
          ) {
            showNotification("Project Code Already Exist", "warning");
          } else {
            showNotification(val?.payload?.data?.message, "warning");
          }
        }
      });
    } else {
      dispatch(createProject(formData)).then((val: any) => {
        if (val?.payload?.data && val?.payload?.data?.statusCode === 200) {
          showNotification("New Project Created in Draft", "success");
          onSubmit();
        }
        if (
          val?.payload?.data &&
          val?.payload?.data?.statusCode === 400 &&
          val?.payload?.data?.message === "PONumberAlreadyExistMessage"
        ) {
          showNotification("PO Number Already Exist", "warning");
        }
        if (
          val?.payload?.data &&
          val?.payload?.data?.statusCode === 400 &&
          val?.payload?.data?.message === "SOWNumberAlreadyExistMessage"
        ) {
          showNotification("SOW Number Already Exist", "warning");
        }
        if (
          val?.payload?.data &&
          val?.payload?.data?.statusCode === 400 &&
          val?.payload?.data?.message === "projectCodeAlreadyExistMessage"
        ) {
          showNotification("Project Code Already Exist", "warning");
        }
      });
    }
  };
  const fetchStaffList = async () => {
    const response = await Client.get(API_GET_ALL_STAFF_LIST);
    if (response?.data?.data) {
      setStaffList(response?.data?.data);
    } else {
      setStaffList([]);
    }
  };
  const fetchDepartmentList = async () => {
    const response = await Client.get(API_GET_ALL_DEPARTMENT_LIST);
    if (response?.data?.data) {
      setDepartmentList(response?.data?.data);
    } else {
      setDepartmentList([]);
    }
  };

  const fetchAccountListByEntity = async (entity: string) => {
    const response = await dispatch(getAccountsByEntity(entity)).unwrap();
    if (response?.data?.data) {
      setAccountList(response?.data?.data);
    } else {
      setAccountList([]);
    }
  };

  useEffect(() => {
    if (selectedEntity) {
      form.setFieldsValue({
        entity: selectedEntity?.entity,
        projectType: selectedEntity?.projectType,
        projectName: selectedEntity?.projectName,
        l2projectName: selectedEntity?.l2ProjectName,
        departmentName: selectedEntity?.departmentId,
        projectManager: selectedEntity?.projectManagerId,
        accountManager: selectedEntity?.accountManagerId,
        status: selectedEntity.status,
        poNumber: selectedEntity?.poNumber,
        projectCode: selectedEntity?.projectCode,
        poValue: selectedEntity?.poValue,
        poCurrency: selectedEntity?.poCurrency,
        sst: selectedEntity?.sst,
        margin: selectedEntity?.margin,
        projectValue: selectedEntity?.projectValue,
        startDate: selectedEntity?.startDate ? dayjs(selectedEntity?.startDate) : null,
        endDate: selectedEntity?.endDate ? dayjs(selectedEntity?.endDate) : null,
        account: selectedEntity?.accountId,
        sowNumber: selectedEntity?.sowNumber,
      });
      setSelectedStartDate(selectedEntity?.startDate ? dayjs(selectedEntity?.startDate) : null);
      fetchAccountListByEntity(selectedEntity?.entity);
    }
  }, [form, selectedEntity]);

  useEffect(() => {
    fetchStaffList();
    fetchDepartmentList();
  }, []);

  const handleDownload = async (file: any) => {
     console.log("file", file);
  if (!file?.uid) {
    console.log("Document ID is not available");
    return;
  }

  try {
    const response = await Client.get(
      `${API_GET_DOCUMENT_SIGNED_URL}${file.uid}`
    );

    const downloadUrl =
      response?.data?.data?.url || response?.data?.url;

    if (!downloadUrl) {
      console.log("Download URL is not available");
      return;
    }

    const link = document.createElement("a");
    link.href = downloadUrl;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    link.download =
      response?.data?.data?.fileName ||
      file.name ||
      "document";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    console.error("Failed to download document:", error);
  }
};

  // Load project documents when the drawer opens with a project (not only on mount —
  // selectedEntity can be set after the first paint, so mount-only fetch missed the first open).
  useEffect(() => {
    if (!visible) {
      setDocuments([]);
      return;
    }
    const projectId = selectedEntity?.projectId;
    if (!projectId) {
      setDocuments([]);
      return;
    }
    let cancelled = false;
    dispatch(getDocuments(projectId))
      .unwrap()
      .then((response: any) => {
        if (!cancelled && response?.data) {
          setDocuments(response.data);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setDocuments([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [visible, selectedEntity?.projectId, dispatch]);

  useEffect(() => {
    if (documents?.length) {
      const converted = documents.map((doc) => ({
        uid: doc.id,
        name: doc.originalFileName,
        status: "done",
        url: doc.bucketPath || "",
      }));

      // Optional: prevent unnecessary state update
      setFileList((prev: any) => {
        const same = JSON.stringify(prev) === JSON.stringify(converted);
        return same ? prev : converted;
      });
    } else {
      setFileList([]); // Clear fileList if documents are empty
    }
  }, [documents]);

  const staffDropdownOptions = staffList.map((staff) => ({
    value: staff?.staffId,
    label: staff?.staffName,
  }));
  const departmentDropdownOptions = departmentList.map((department) => ({
    value: department?.departmentId,
    label: department?.departmentName,
  }));
  const accountDropdownOptions = accountList.map((account) => ({
    value: account?.accountId,
    label: account?.accountName,
  }));

  const onFinishFailed = (_errorInfo: any) => {
    return;
  };

  const onValuesChange = (changedValues: any, _allValues: any) => {
    if ("startDate" in changedValues) {
      setSelectedStartDate(_allValues.startDate);
    }
    if ("entity" in changedValues) {
      fetchAccountListByEntity(_allValues.entity);
      // Clear the previously selected client when entity changes
      form.setFieldsValue({ account: undefined });
    }
    if ("sst" in changedValues || "poValue" in changedValues) {
      const sst = _allValues.sst || 0;
      const poValue = _allValues.poValue || 0;
      const projectValue =
        Number(poValue) + (Number(poValue) * Number(sst)) / 100;
      form.setFieldsValue({ projectValue });
    }
  };
  const disabledDate = (current: any) => {
    // Disable dates before the start date
    return !!(current && selectedStartDate && current < dayjs(selectedStartDate).endOf("day"));
  };

  const handleUploadChange = (info: any) => {
    const { file, fileList } = info;

    // Optional: filter duplicates by UID or name
    const uniqueFileList = fileList.filter(
      (file: { uid: any }, index: any, self: any[]) =>
        index === self.findIndex((f) => f.uid === file.uid)
    );
    // .map((file: { originFileObj: any; }) => file.originFileObj);

    setFileList(uniqueFileList);

    if (file.status === "done" || !file.status) {
      showNotification(`${file.name} file uploaded successfully.`, "success");
    } else if (file.status === "error") {
      showNotification(`${file.name} file upload failed.`, "error");
    }
  };

  const handleRemove = (file: any) => {
    // const updatedList = fileList.filter((f) => f.uid !== file.uid);
    // setFileList(updatedList);
  };
  const handleCustomRequest = ({ file, onSuccess }: any) => {
    // Simulate upload success after 0ms
    setTimeout(() => {
      onSuccess("ok");
    }, 0);
  };

  const beforeUpload = async (file: any) => {
    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];

    const isValidType = allowedTypes.includes(file.type);

    if (!isValidType) {
      showNotification("Only PDF or Word files are allowed", "warning");
      return Upload.LIST_IGNORE;
    }

    return false; // Prevent auto upload if you're using customRequest
  };
  var colSpan = screenType.isPortrait ? 24 : 12;
  const formProps = {
    form: form,
    name: "creat-edit-project",
    heading: selectedEntity ? "Edit Project Details" : "Add New Project",
    initialValues: {
      status: selectedEntity?.status || "DRAFT",
      poValue: 0
    },
    formElements: [
      {
        name: "entity",
        colSpan: 12,
        label: "Entity",
        placeholder: "Entity ",
        type: "Select",
        allowClear: true,
        rules: [{ required: true, message: "Please select Entity" }],
        options: entityList?.map((entity: any) => ({
          value: entity?.id,
          label: entity?.name,
        })),
      },
      {
        name: "account",
        label: "Client",
        placeholder: "Client",
        type: "Select",
        colSpan: colSpan,
        showSearch: true,
        allowClear: true,
        options: accountDropdownOptions,
        rules: [{ required: true, message: "Please enter Client" }],
      },
      {
        name: "sowNumber",
        colSpan: 12,
        label: "SOW/Quotation (Internal)",
        placeholder: "Enter SOW/Quotation (Internal)",
        type: "Input",
        allowClear: true,
        rules: [{ required: true, message: "Please enter SOW/Quotation (Internal)" }],
      },
      {
        name: "poNumber",
        colSpan: 12,
        label: "PO Number (Client Provided)",
        placeholder: "Enter PO Number (Client Provided)",
        type: "Input",
        allowClear: true,
        rules: [{ required: true, message: "Please enter PO Number (Client Provided)" }],
      },
      {
        name: "projectType",
        label: "Project Type",
        placeholder: "Project Type",
        type: "Select",
        colSpan: colSpan,
        showSearch: true,
        allowClear: true,
        options: projectTypes?.map((type: any) => ({
          value: type?.id,
          label: type?.name,
        })),
        rules: [{ required: true, message: "Please enter Project Type" }],
      },
      {
        name: "l2projectName",
        colSpan: 12,
        label: "Project Name",
        placeholder: "Enter Project Name ",
        type: "Input",
        allowClear: true,
        rules: [{ required: true, message: "Please enter Project Name" }],
      },
      // {
      //   name: "l2projectName",
      //   colSpan: 12,
      //   label: "L2 Project Name",
      //   placeholder: "Enter L2 Project Name ",
      //   type: "Input",
      //   allowClear: true,
      //   rules: [{ required: true, message: "Please enter L2 Project Name" }],
      // },
      selectedEntity?.status !== "DRAFT" && selectedEntity && {
        name: "status",
        label: "status",
        placeholder: "status",
        type: "Select",
        colSpan: colSpan,
        showSearch: true,
        allowClear: true,
        options:
          selectedEntity?.status === "REJECTED"
            ? [{ value: "DRAFT", label: "DRAFT" }]
            : [
                { value: "DRAFT", label: "DRAFT" },
                { value: "ACTIVE", label: "ACTIVE" },
                { value: "INACTIVE", label: "INACTIVE" },
              ],
        rules: [{ required: true, message: "Please enter Status" }],
      },
      {
        name: "startDate",
        label: "Start Date",
        placeholder: "Start Date",
        type: "DatePicker",
        colSpan: colSpan,
        showSearch: true,
        allowClear: true,
        rules: [{ required: true, message: "Please enter Start Date" }],
      },
      {
        name: "endDate",
        label: "End Date",
        placeholder: "End Date",
        type: "DatePicker",
        colSpan: colSpan,
        showSearch: true,
        allowClear: true,
        dependencies: ["startDate"],
        rules: [
          { required: true, message: "Please enter End Date" },
          ({ getFieldValue }: any) => ({
            validator(_: any, value: any) {
              const startDate = getFieldValue("startDate");

              if (!value || !startDate || !dayjs(value).isBefore(dayjs(startDate), "day")) {
                return Promise.resolve();
              }

              return Promise.reject(
                new Error("End Date cannot be earlier than Start Date")
              );
            },
          }),
        ],
        disabledDate: disabledDate,
      },
      {
        name: "accountManager",
        label: "Account Manager Name",
        placeholder: "Account Manager Name",
        type: "Select",
        colSpan: colSpan,
        showSearch: true,
        allowClear: true,
        options: staffDropdownOptions,
        rules: [
          { required: true, message: "Please enter Account Manager Name" },
        ],
      },
      {
        name: "projectManager",
        label: "Project Manager Name",
        placeholder: "Project Manager Name",
        type: "Select",
        colSpan: colSpan,
        showSearch: true,
        allowClear: true,
        options: staffDropdownOptions,
        rules: [
          { required: true, message: "Please enter Project Manager Name" },
        ],
      },
      // {
      //   name: "projectCode",
      //   colSpan: 12,
      //   label: "Project Code",
      //   placeholder: "Enter Project Code ",
      //   type: "Input",
      //   allowClear: true,
      //   rules: [{ required: false, message: "Please enter Project Code" }],
      // },
      {
        name: "poCurrency",
        label: "PO Currency",
        placeholder: "PO Currency",
        type: "Select",
        colSpan: colSpan,
        showSearch: true,
        allowClear: true,
        options: currencies?.map((Currency: any) => ({
          value: Currency?.id,
          label: Currency?.name,
        })),
        rules: [{ required: true, message: "Please enter PO Currency" }],
      },
      {
        name: "poValue",
        colSpan: 12,
        label: "PO Value",
        placeholder: "Enter PO Value",
        type: "InputNumber",
        allowClear: true,
        min: 0,
        // prefix: "RM",
        isAmount: true,
        rules: [{ required: true, message: "Please enter PO Value" }],
      },
      // {
      //   name: "sst",
      //   colSpan: 12,
      //   label: "SST",
      //   placeholder: "Enter SST",
      //   type: "InputNumber",
      //   allowClear: true,
      //   rules: [{ required: true, message: "Please enter SST" }],
      // },
      {
        name: "margin",
        colSpan: 12,
        label: "Margin",
        placeholder: "Enter Margin",
        type: "Input",
        allowClear: true,
        suffix: "%",
        rules: [
          { required: true, message: "Please enter Margin" },
          {
            validator: (_: any, value: any) => {
              if (value < 0 || value > 100) {
                return Promise.reject("Margin must be between 0 and 100");
              }
              return Promise.resolve();
            },
          },
        ],
        fieldProps: {
          onChange: (e: any) => {
            const onlyNums = e.target.value.replace(/\D/g, "");
            e.target.value = onlyNums; // optional - doesn't control input
            e.currentTarget.value = onlyNums; // for React event
          },
          inputMode: "numeric", // mobile-friendly numeric keypad
          onKeyDown: (e: any) => {
            // Prevent typing non-numeric characters
            if (
              !/[0-9]/.test(e.key) &&
              e.key !== "Backspace" &&
              e.key !== "Delete" &&
              e.key !== "ArrowLeft" &&
              e.key !== "ArrowRight"
            ) {
              e.preventDefault();
            }
          },
        },
      },
      // {
      //   name: "projectValue",
      //   colSpan: 12,
      //   label: "Total Project Value",
      //   placeholder: "Total Project Value",
      //   type: "InputNumber",
      //   allowClear: true,
      //   disabled: true,
      //   prefix: "RM",
      //   rules: [{ required: false, message: "Please enter Project Value" }],
      // },
      // {
      //   name: "departmentName",
      //   label: "Department Name",
      //   placeholder: "Department Name",
      //   type: "Select",
      //   colSpan: colSpan,
      //   showSearch: true,
      //   allowClear: true,
      //   options: departmentDropdownOptions,
      //   rules: [{ required: false, message: "Please enter Department Name" }],
      // },
      {
        name: "files", // or any identifier
        label: "Upload File",
        type: "Custom",
        colSpan: 16,
        // The dragger is driven by the `fileList` state below, not by the form
        // store, so it is not wrapped in its own Form.Item — an inner Form.Item
        // would inject `fileList` onto this plain div and React would warn
        // about an unrecognised DOM prop.
        render: ({} = {}) => (
            <div style={{ maxHeight: 400 }}>
              <Upload.Dragger
                name="file"
                multiple={true}
                listType="text"
                showUploadList={{ showRemoveIcon: true }}
                fileList={fileList}
                onChange={handleUploadChange}
                onRemove={handleRemove}
                customRequest={handleCustomRequest}
                beforeUpload={beforeUpload}
                accept=".pdf,.doc,.docx"
                style={{ padding: "16px 0", backgroundColor: "#dcdcdc" }}
                // onPreview={async (file) => {
                //   try {
                //     if (file.url) {
                //       const res = await dispatch(
                //         getSignedUrl({
                //           module: "project",
                //           params: { url: file.url },
                //         })
                //       ).unwrap();

                //       if (res?.data?.url) {
                //         window.open(res.data.url, "_blank");
                //       }
                //     }
                //   } catch (e) {
                //     console.log("Could not open file");
                //   }
                // }}
                onPreview={async (file) => {
                  await handleDownload(file);
                }}
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">
                  Click or drag file to this area to upload
                </p>
              </Upload.Dragger>
            </div>
        ),
      },
    ],
    extraButtons: [
      {
        htmlType: "button",
        colSpan: 5,
        loading: false,
        submitButtonText: "Cancel",
        onClick: onClose,
      },
      {
        htmlType: "button",
        colSpan: 5,
        loading: false,
        submitButtonText: "Clear",
        type: "clearForm",
      },
      {
        htmlType: "submit",
        colSpan: 5,
        loading: false,
        submitButtonText: "Save",
        submitButtonType: "primary",
      },
    ],
    onFinish: onFinish,
    onFinishFailed: onFinishFailed,
    onValuesChange: onValuesChange,
  };
  return (
    <>
      <Drawer
        maskClosable={false}
        title={<strong>{formProps.heading}</strong>}
        className="form-drawer"
        width={
          screenType.isPortrait
            ? "90%"
            : screenType.isLg || screenType.isXl
            ? 720
            : 500
        }
        onClose={onClose}
        open={visible}
      >
        <GenericFormComponent {...formProps} />
      </Drawer>
    </>
  );
};

export default CreateEditProjectComponent;
